import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { setupMedical, getCoverageMaster } from '../../../services/api';
import {
  medicalInitialState,
  coverageMasterToState,
  slotForSpecialty,
  SPECIALTIES,
} from '../../../lib/data/medicalPresets';
import StepHours from '../StepHours';
import SpecialtyPicker from './SpecialtyPicker';
import MedicalLicenseInput, { isValidLicense } from './MedicalLicenseInput';
import CoverageMatrix from './CoverageMatrix';
import DPAConsentEditor from './DPAConsentEditor';
import PatientConsentTemplateEditor from './PatientConsentTemplateEditor';
import '../SetupWizard.css';
import './MedicalSetupWizard.css';

const TOTAL_STEPS = 8;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Validación por paso — devuelve { fieldKey: 'errorKey' } vacío si el paso es OK.
function validateStep(step, s) {
  const e = {};
  if (step === 1) {
    if (!s.compliance.termsAccepted) e.terms = 'required';
    if (!s.compliance.dpaAccepted) e.dpa = 'required';
  }
  if (step === 2) {
    if (!s.professional.fullName.trim()) e.fullName = 'required';
    if (!s.professional.email.trim()) e.email = 'required';
    else if (!EMAIL_RE.test(s.professional.email.trim())) e.email = 'invalidEmail';
    if (!s.professional.specialty) e.specialty = 'required';
    else if (s.professional.specialty === 'otro' && !s.professional.specialtyOther.trim())
      e.specialty = 'required';
    if (!isValidLicense(s.professional.license) || !s.professional.licenseProvince) e.license = 'required';
  }
  if (step === 3) {
    if (!s.clinic.name.trim()) e.clinicName = 'required';
    if (!s.clinic.province) e.clinicProvince = 'required';
  }
  if (step === 4) {
    const bad = s.coverages.some(
      (c) => c.enabled && (c.copayArs === '' || c.copayArs === null || isNaN(Number(c.copayArs)))
    );
    if (bad) e.coverages = 'invalid';
    if (s.coverages.some((c) => c.custom && c.enabled && !c.name.trim())) e.coverages = 'invalid';
  }
  if (step === 5) {
    if (!s.consultationTypes.length) e.consultationTypes = 'minOne';
    else if (s.consultationTypes.some((c) => !c.name.trim() || !(Number(c.durationMin) > 0)))
      e.consultationTypes = 'invalid';
  }
  if (step === 6) {
    const h = s.hours || {};
    if (!h.days || !h.days.length) e.hours = 'minOneDay';
    else if (h.customPerDay) {
      const invalid = h.days.some((d) => {
        const row = h.customPerDay[d] || {};
        return !(row.openTime < row.closeTime);
      });
      if (invalid) e.hours = 'openBeforeClose';
    } else if (!(h.openTime < h.closeTime)) e.hours = 'openBeforeClose';
  }
  if (step === 7) {
    if (!s.whatsapp.number.trim()) e.whatsapp = 'required';
    if (!s.whatsapp.patientConsentTemplate.trim()) e.consentTemplate = 'required';
  }
  return e;
}

// Arma el payload para POST /admin/consultame/setup-medical.
function buildPayload(s) {
  const specialtyLabel =
    s.professional.specialty === 'otro'
      ? s.professional.specialtyOther.trim()
      : (SPECIALTIES.find((x) => x.key === s.professional.specialty) || {}).label;
  return {
    vertical: 'medical',
    compliance: {
      termsAccepted: s.compliance.termsAccepted,
      dpaAccepted: s.compliance.dpaAccepted,
      dpaVersion: s.compliance.dpaVersion,
      dpaText: s.compliance.dpaText,
    },
    professional: {
      fullName: s.professional.fullName.trim(),
      email: s.professional.email.trim(),
      specialty: s.professional.specialty,
      specialtyLabel,
      license: s.professional.license.trim(),
      licenseProvince: s.professional.licenseProvince,
      cuil: s.professional.cuil.trim() || null,
    },
    clinic: {
      name: s.clinic.name.trim(),
      address: s.clinic.address.trim(),
      phone: s.clinic.phone.trim(),
      city: s.clinic.city.trim(),
      province: s.clinic.province,
    },
    coverages: s.coverages
      .filter((c) => c.enabled)
      .map((c) => ({ key: c.key, name: c.name.trim(), copayArs: Number(c.copayArs) || 0 })),
    consultationTypes: s.consultationTypes.map((c) => ({
      key: c.key,
      name: c.name.trim(),
      durationMin: Number(c.durationMin),
      priceArs: c.priceArs === '' || c.priceArs === null ? null : Number(c.priceArs),
      whatsappEnabled: !!c.whatsappEnabled,
    })),
    hours: {
      days: s.hours.days,
      openTime: s.hours.openTime,
      closeTime: s.hours.closeTime,
      customPerDay: s.hours.customPerDay,
    },
    whatsapp: {
      number: s.whatsapp.number.trim(),
      privacyNoticeEnabled: s.whatsapp.privacyNoticeEnabled,
      acceptsUrgencies: s.whatsapp.acceptsUrgencies,
      patientConsentTemplate: s.whatsapp.patientConsentTemplate.trim(),
    },
    settings: { timezone: s.settings.timezone },
  };
}

function MedicalSetupWizard() {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [state, setState] = useState(medicalInitialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(true);

  const update = useCallback((patch) => {
    setState((prev) => (typeof patch === 'function' ? patch(prev) : { ...prev, ...patch }));
  }, []);

  // Carga el catálogo autoritativo de obras sociales del backend; ante error usa
  // el fallback local (medicalPresets) para no bloquear el onboarding.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await getCoverageMaster();
        const master = Array.isArray(data) ? data : data?.coverages;
        if (alive && master && master.length) {
          setState((prev) => ({ ...prev, coverages: coverageMasterToState(master) }));
        }
      } catch (err) {
        // Silencioso: el fallback ya está cargado en el estado inicial.
      } finally {
        if (alive) setCoverageLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const onSelectSpecialty = useCallback(({ specialty, specialtyOther }) => {
    setState((prev) => {
      const slot = slotForSpecialty(specialty);
      const consultationTypes = prev.consultationTypes.map((c) =>
        c.key === 'primera' ? { ...c, durationMin: Math.max(c.durationMin, slot) } : c
      );
      return {
        ...prev,
        professional: { ...prev.professional, specialty, specialtyOther },
        consultationTypes,
      };
    });
    setErrors((e) => ({ ...e, specialty: undefined }));
  }, []);

  // Al setear provincia del consultorio en Buenos Aires, pre-activa IOMA (spec §Step4).
  const onSelectProvince = useCallback((province) => {
    setState((prev) => {
      const coverages =
        province === 'Buenos Aires'
          ? prev.coverages.map((c) => (c.key === 'ioma' && !c.enabled ? { ...c, enabled: true } : c))
          : prev.coverages;
      return { ...prev, clinic: { ...prev.clinic, province }, coverages };
    });
    setErrors((e) => ({ ...e, clinicProvince: undefined }));
  }, []);

  const goNext = useCallback(() => {
    const stepErrors = validateStep(step, state);
    setErrors(stepErrors);
    if (Object.keys(stepErrors).length > 0) return;
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  }, [step, state]);

  const goBack = useCallback(() => {
    setErrors({});
    setStep((s) => Math.max(1, s - 1));
  }, []);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const data = await setupMedical(buildPayload(state));
      setResult(data);
    } catch (err) {
      const resp = err?.response?.data;
      const status = err?.response?.status;
      let message = resp?.error || err?.message || 'setup_failed';
      if (status === 409) message = t('setupWizardMedical.errors.userExists', 'Ya existe un usuario con ese email.');
      else if (status === 400) message = resp?.error || t('setupWizardMedical.errors.validation', 'Revisá los datos: hay campos inválidos.');
      setSubmitError({ message, step: resp?.step || null, status: status || null });
    } finally {
      setSubmitting(false);
    }
  }, [state, t]);

  const setPro = (patch) => update((p) => ({ ...p, professional: { ...p.professional, ...patch } }));
  const setClinic = (patch) => update((p) => ({ ...p, clinic: { ...p.clinic, ...patch } }));
  const setWa = (patch) => update((p) => ({ ...p, whatsapp: { ...p.whatsapp, ...patch } }));

  const enabledCoverages = state.coverages.filter((c) => c.enabled);

  if (result) {
    const adminLink = `/?org=${encodeURIComponent(result.orgId || '')}&vertical=medical`;
    return (
      <div className="setup-wizard med-wizard">
        <div className="sw-result sw-result-success">
          <span className="material-icons sw-result-icon">check_circle</span>
          <h2>{t('setupWizardMedical.success.title', '¡Listo! Consultorio creado')}</h2>
          <p>
            {t('setupWizardMedical.success.invite', {
              email: result.ownerEmail || state.professional.email,
              defaultValue: 'Enviamos el acceso a {{email}}.',
            })}
          </p>
          <div className="med-compliance-checklist">
            <span><span className="material-icons">check</span> {t('setupWizardMedical.review.dpaOk', 'DPA aceptado')}</span>
            <span><span className="material-icons">check</span> {t('setupWizardMedical.review.privacyOk', 'Aviso de privacidad activo')}</span>
            <span><span className="material-icons">check</span> {t('setupWizardMedical.review.coverageOk', 'Obras sociales configuradas')}</span>
          </div>
          <a className="btn btn-primary" href={adminLink}>
            {t('setupWizardMedical.success.goToAdmin', 'Ir al panel del consultorio')}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="setup-wizard med-wizard">
      <header className="sw-header">
        <h1 className="sw-title">{t('setupWizardMedical.title', 'Configurá tu consultorio — Consultame')}</h1>
        <div className="sw-progress" aria-label={t('setupWizard.progressLabel', 'Progreso')}>
          {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((n) => (
            <React.Fragment key={n}>
              <span
                className={`sw-step-dot ${n === step ? 'current' : ''} ${n < step ? 'done' : ''}`}
                aria-current={n === step ? 'step' : undefined}
              >
                {n < step ? <span className="material-icons">check</span> : n}
              </span>
              {n < TOTAL_STEPS && <span className="sw-step-line" />}
            </React.Fragment>
          ))}
        </div>
        <span className="sw-step-count">
          {t('setupWizard.stepCount', { current: step, total: TOTAL_STEPS, defaultValue: 'Paso {{current}} de {{total}}' })}
        </span>
      </header>

      <div className="sw-body">
        {step === 1 && (
          <div className="sw-step">
            <h2 className="sw-step-title">{t('setupWizardMedical.step1.title', 'Bienvenido a Consultame')}</h2>
            <p className="sw-hint">
              {t('setupWizardMedical.step1.brief', 'Consultame cumple con la Ley 25.326 de Protección de Datos Personales. Los datos de tus pacientes son 100% tuyos.')}
            </p>
            <label className="med-consent-check">
              <input
                type="checkbox"
                checked={state.compliance.termsAccepted}
                onChange={(e) => update((p) => ({ ...p, compliance: { ...p.compliance, termsAccepted: e.target.checked } }))}
              />
              <span>{t('setupWizardMedical.step1.terms', 'Acepto los Términos y Condiciones y la Política de Privacidad.')}</span>
            </label>
            {errors.terms && <p className="sw-error">{t('setupWizardMedical.errors.termsRequired', 'Tenés que aceptar los términos.')}</p>}
            <DPAConsentEditor
              text={state.compliance.dpaText}
              version={state.compliance.dpaVersion}
              editable={false}
              accepted={state.compliance.dpaAccepted}
              onAccept={(v) => update((p) => ({ ...p, compliance: { ...p.compliance, dpaAccepted: v } }))}
            />
            {errors.dpa && <p className="sw-error">{t('setupWizardMedical.errors.dpaRequired', 'Tenés que aceptar el DPA.')}</p>}
          </div>
        )}

        {step === 2 && (
          <div className="sw-step">
            <h2 className="sw-step-title">{t('setupWizardMedical.step2.title', 'Perfil del profesional')}</h2>
            <div className="med-field">
              <label className="med-label" htmlFor="pro-name">{t('setupWizardMedical.professional.fullName', 'Nombre completo')}</label>
              <input id="pro-name" type="text" className="sw-input" value={state.professional.fullName}
                onChange={(e) => setPro({ fullName: e.target.value })} placeholder="Dra. Ana López" />
              {errors.fullName && <p className="sw-error">{t('setupWizardMedical.errors.required', 'Campo requerido')}</p>}
            </div>
            <div className="med-field">
              <label className="med-label" htmlFor="pro-email">{t('setupWizardMedical.professional.email', 'Email del profesional (acceso al panel)')}</label>
              <input id="pro-email" type="email" className="sw-input" value={state.professional.email}
                onChange={(e) => setPro({ email: e.target.value })} placeholder="ana@consultorio.com" />
              {errors.email === 'required' && <p className="sw-error">{t('setupWizardMedical.errors.required', 'Campo requerido')}</p>}
              {errors.email === 'invalidEmail' && <p className="sw-error">{t('setupWizardMedical.errors.invalidEmail', 'Email inválido')}</p>}
            </div>
            <SpecialtyPicker
              value={state.professional.specialty}
              otherValue={state.professional.specialtyOther}
              onChange={onSelectSpecialty}
              error={errors.specialty}
            />
            <MedicalLicenseInput
              value={state.professional.license}
              province={state.professional.licenseProvince}
              onChange={({ license, licenseProvince }) => setPro({ license, licenseProvince })}
              error={errors.license}
            />
            <div className="med-field">
              <label className="med-label" htmlFor="pro-cuil">{t('setupWizardMedical.professional.cuil', 'CUIL (opcional, para futura facturación)')}</label>
              <input id="pro-cuil" type="text" className="sw-input" value={state.professional.cuil}
                onChange={(e) => setPro({ cuil: e.target.value })} placeholder="27-12345678-4" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="sw-step">
            <h2 className="sw-step-title">{t('setupWizardMedical.step3.title', 'Datos del consultorio')}</h2>
            <div className="med-field">
              <label className="med-label" htmlFor="cl-name">{t('setupWizardMedical.clinic.name', 'Nombre del consultorio / práctica')}</label>
              <input id="cl-name" type="text" className="sw-input" value={state.clinic.name}
                onChange={(e) => setClinic({ name: e.target.value })} placeholder="Consultorio Belgrano" />
              {errors.clinicName && <p className="sw-error">{t('setupWizardMedical.errors.required', 'Campo requerido')}</p>}
            </div>
            <div className="med-field">
              <label className="med-label" htmlFor="cl-addr">{t('setupWizardMedical.clinic.address', 'Dirección')}</label>
              <input id="cl-addr" type="text" className="sw-input" value={state.clinic.address}
                onChange={(e) => setClinic({ address: e.target.value })} placeholder="Av. Cabildo 1234, Piso 3" />
            </div>
            <div className="med-field">
              <label className="med-label" htmlFor="cl-phone">{t('setupWizardMedical.clinic.phone', 'Teléfono del consultorio')}</label>
              <input id="cl-phone" type="text" className="sw-input" value={state.clinic.phone}
                onChange={(e) => setClinic({ phone: e.target.value })} placeholder="011 4788-0000" />
            </div>
            <div className="med-field med-field-grid">
              <div>
                <label className="med-label" htmlFor="cl-city">{t('setupWizardMedical.clinic.city', 'Ciudad')}</label>
                <input id="cl-city" type="text" className="sw-input" value={state.clinic.city}
                  onChange={(e) => setClinic({ city: e.target.value })} placeholder="CABA" />
              </div>
              <div>
                <label className="med-label" htmlFor="cl-prov">{t('setupWizardMedical.clinic.province', 'Provincia')}</label>
                <select id="cl-prov" className="sw-input" value={state.clinic.province}
                  onChange={(e) => onSelectProvince(e.target.value)}>
                  <option value="" disabled>{t('setupWizardMedical.clinic.provincePlaceholder', 'Elegí provincia')}</option>
                  {['Buenos Aires', 'CABA'].concat(
                    ['Catamarca','Chaco','Chubut','Córdoba','Corrientes','Entre Ríos','Formosa','Jujuy','La Pampa','La Rioja','Mendoza','Misiones','Neuquén','Río Negro','Salta','San Juan','San Luis','Santa Cruz','Santa Fe','Santiago del Estero','Tierra del Fuego','Tucumán']
                  ).map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                {errors.clinicProvince && <p className="sw-error">{t('setupWizardMedical.errors.required', 'Campo requerido')}</p>}
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="sw-step">
            <h2 className="sw-step-title">{t('setupWizardMedical.step4.title', 'Obras sociales aceptadas')}</h2>
            <CoverageMatrix
              coverages={state.coverages}
              onChange={(coverages) => update({ coverages })}
              loading={coverageLoading}
              error={errors.coverages}
            />
          </div>
        )}

        {step === 5 && (
          <div className="sw-step">
            <h2 className="sw-step-title">{t('setupWizardMedical.step5.title', 'Tipos de consulta')}</h2>
            <div className="med-consult-list">
              {state.consultationTypes.map((c, idx) => (
                <div key={c.key} className="med-consult-row">
                  <input type="text" className="sw-input med-consult-name" value={c.name}
                    placeholder={t('setupWizardMedical.consult.name', 'Nombre')}
                    onChange={(e) => update((p) => ({ ...p, consultationTypes: p.consultationTypes.map((x, i) => i === idx ? { ...x, name: e.target.value } : x) }))} />
                  <label className="med-consult-field">
                    <span>{t('setupWizardMedical.consult.duration', 'Min')}</span>
                    <input type="number" min="5" className="sw-input" value={c.durationMin}
                      onChange={(e) => update((p) => ({ ...p, consultationTypes: p.consultationTypes.map((x, i) => i === idx ? { ...x, durationMin: e.target.value === '' ? '' : Number(e.target.value) } : x) }))} />
                  </label>
                  <label className="med-consult-field">
                    <span>ARS</span>
                    <input type="number" min="0" className="sw-input" value={c.priceArs}
                      onChange={(e) => update((p) => ({ ...p, consultationTypes: p.consultationTypes.map((x, i) => i === idx ? { ...x, priceArs: e.target.value === '' ? '' : Number(e.target.value) } : x) }))} />
                  </label>
                  <label className="med-consult-toggle" title={t('setupWizardMedical.consult.whatsapp', 'Disponible por WhatsApp')}>
                    <input type="checkbox" checked={c.whatsappEnabled}
                      onChange={(e) => update((p) => ({ ...p, consultationTypes: p.consultationTypes.map((x, i) => i === idx ? { ...x, whatsappEnabled: e.target.checked } : x) }))} />
                    <span className="material-icons">chat</span>
                  </label>
                  {state.consultationTypes.length > 1 && (
                    <button type="button" className="btn btn-ghost btn-sm sw-icon-btn"
                      onClick={() => update((p) => ({ ...p, consultationTypes: p.consultationTypes.filter((_, i) => i !== idx) }))}
                      aria-label={t('setupWizardMedical.consult.remove', 'Quitar')}>
                      <span className="material-icons">close</span>
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" className="btn btn-secondary btn-sm med-add-coverage"
              onClick={() => update((p) => ({ ...p, consultationTypes: [...p.consultationTypes, { key: `custom_${p.consultationTypes.length}_${Date.now()}`, name: '', durationMin: 30, priceArs: '', whatsappEnabled: true }] }))}>
              <span className="material-icons">add</span>
              {t('setupWizardMedical.consult.add', 'Agregar tipo de consulta')}
            </button>
            {errors.consultationTypes && <p className="sw-error">{t('setupWizardMedical.errors.consultInvalid', 'Completá nombre y duración de cada tipo de consulta.')}</p>}
          </div>
        )}

        {step === 6 && (
          <StepHours state={state} errors={errors} onChange={(hours) => update({ hours })} />
        )}

        {step === 7 && (
          <div className="sw-step">
            <h2 className="sw-step-title">{t('setupWizardMedical.step7.title', 'WhatsApp del consultorio')}</h2>
            <div className="med-field">
              <label className="med-label" htmlFor="wa-number">{t('setupWizardMedical.whatsapp.number', 'Número de WhatsApp del bot')}</label>
              <input id="wa-number" type="text" className="sw-input" value={state.whatsapp.number}
                onChange={(e) => setWa({ number: e.target.value })} placeholder="+54 9 11 5555-5555" />
              {errors.whatsapp && <p className="sw-error">{t('setupWizardMedical.errors.required', 'Campo requerido')}</p>}
            </div>
            <PatientConsentTemplateEditor
              value={state.whatsapp.patientConsentTemplate}
              onChange={(patientConsentTemplate) => setWa({ patientConsentTemplate })}
              previewValues={{ consultorio: state.clinic.name, email: state.professional.email }}
            />
            {errors.consentTemplate && <p className="sw-error">{t('setupWizardMedical.errors.required', 'Campo requerido')}</p>}
            <label className="med-consent-check med-field-spaced">
              <input type="checkbox" checked={state.whatsapp.privacyNoticeEnabled} disabled
                onChange={() => {}} />
              <span>{t('setupWizardMedical.whatsapp.privacyNotice', 'Activar aviso de privacidad automático en primer contacto (obligatorio).')}</span>
            </label>
            <label className="med-consent-check">
              <input type="checkbox" checked={state.whatsapp.acceptsUrgencies}
                onChange={(e) => setWa({ acceptsUrgencies: e.target.checked })} />
              <span>{t('setupWizardMedical.whatsapp.urgencies', '¿Aceptás urgencias fuera de horario?')}</span>
            </label>
          </div>
        )}

        {step === 8 && (
          <div className="sw-step">
            <h2 className="sw-step-title">{t('setupWizardMedical.step8.title', 'Revisión y activación')}</h2>
            <dl className="med-review">
              <div><dt>{t('setupWizardMedical.review.professional', 'Profesional')}</dt><dd>{state.professional.fullName} — {state.professional.specialty === 'otro' ? state.professional.specialtyOther : (SPECIALTIES.find((x) => x.key === state.professional.specialty) || {}).label}</dd></div>
              <div><dt>{t('setupWizardMedical.review.clinic', 'Consultorio')}</dt><dd>{state.clinic.name}, {state.clinic.province}</dd></div>
              <div><dt>{t('setupWizardMedical.review.coverages', 'Obras sociales')}</dt><dd>{enabledCoverages.map((c) => c.name).join(', ') || '—'}</dd></div>
              <div><dt>{t('setupWizardMedical.review.consults', 'Tipos de consulta')}</dt><dd>{state.consultationTypes.map((c) => c.name).filter(Boolean).join(', ')}</dd></div>
              <div><dt>{t('setupWizardMedical.review.whatsapp', 'WhatsApp')}</dt><dd>{state.whatsapp.number || '—'}</dd></div>
            </dl>
            <div className="med-compliance-checklist">
              <span><span className="material-icons">check_circle</span> {t('setupWizardMedical.review.dpaOk', 'DPA aceptado')}</span>
              <span><span className="material-icons">check_circle</span> {t('setupWizardMedical.review.privacyOk', 'Aviso de privacidad activo')}</span>
              <span><span className="material-icons">check_circle</span> {t('setupWizardMedical.review.coverageOk', 'Obras sociales configuradas')}</span>
            </div>
            {submitError && (
              <p className="sw-error">
                {submitError.message}
                {submitError.status ? ` (${submitError.status})` : ''}
              </p>
            )}
            <button type="button" className="btn btn-primary med-submit" onClick={handleSubmit} disabled={submitting}>
              {submitting
                ? t('setupWizardMedical.step8.submitting', 'Activando…')
                : t('setupWizardMedical.step8.submit', 'Activar Consultame')}
            </button>
          </div>
        )}
      </div>

      <footer className="sw-nav">
        {step > 1 ? (
          <button type="button" className="btn btn-ghost" onClick={goBack} disabled={submitting}>
            {t('setupWizard.back', '← Atrás')}
          </button>
        ) : (
          <span />
        )}
        {step < TOTAL_STEPS && (
          <button type="button" className="btn btn-primary" onClick={goNext}>
            {t('setupWizard.next', 'Siguiente →')}
          </button>
        )}
      </footer>
    </div>
  );
}

export default MedicalSetupWizard;
